import { useQuery } from "@tanstack/react-query";
import { DropsApi } from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * The Drop's creator roster with its live game filter applied.
 *
 * Distinct from useGetDropLiveStatus, which covers the Drop's OWN channels.
 * This is the roster — the publisher's official creator program — and only
 * this endpoint can apply the Drop's campaign window, because windows live on
 * the Drop rather than on the roster.
 *
 * Returns the counts the page needs to make the filter legible: `hiddenOffGame`
 * backs the "N team members are live on other titles — hidden" line, without
 * which a filtered roster just looks quiet.
 *
 * Polls on the same cadence as live status — the backend caches for 30s, so a
 * 60s refetch never stampedes it.
 */
export function useGetDropRoster(slug?: string) {
    return useQuery({
        queryKey: ["drop-roster", slug],
        queryFn: async () => {
            if (!slug) return null;
            const api = new DropsApi(getConfig());
            /*
             * No cast. The generated client already types this — the admin hook
             * calls it the same way — and `as any` discarded the return type for
             * the entire roster feature, so nothing downstream was checked
             * against the schema. That matters here specifically: the off-game
             * suppression flipped several member fields to optional, and a
             * consumer reading them unguarded would no longer be caught.
             */
            return api.getDropsSlugSlugRoster({slug});
        },
        enabled: Boolean(slug),
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}
